import React from 'react';
import { Drawer, Form, Input, Button, message, Descriptions, Tag } from 'antd';
import { LockOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { getUserById, updateUser } from '../../api/sys_user';
import { getUser, setAuth, getToken } from '../../utils/auth';
import { displayRoleName, ROLE_TAG_COLOR } from '../../utils/role';
import { drawerWidth } from '../../utils/responsive';
import {
    passwordFormValidator,
    PASSWORD_RULE_HINT,
} from '../../utils/passwordPolicy';

const PHONE_RULE = {
    pattern: /^$|^1[3-9]\d{9}$/,
    message: '请输入有效的 11 位手机号，或留空',
};

/** 顶栏点击账号时打开，编辑当前登录用户资料 */
class ProfileEditDrawer extends React.Component {
    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            loading: false,
            submitting: false,
            profile: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.loadProfile();
        }
    }

    loadProfile = async () => {
        const current = getUser();
        if (!current?.id || !getToken()) return;
        this.setState({ loading: true });
        try {
            const data = await getUserById(current.id);
            this.setState({ profile: data });
            this.formRef.current?.setFieldsValue({
                email: data.email || '',
                phone: data.phone || '',
                password: '',
            });
        } catch (e) {
            message.error(e.message || '加载个人信息失败');
        } finally {
            this.setState({ loading: false });
        }
    };

    onFinish = async (values) => {
        const current = getUser();
        this.setState({ submitting: true });
        try {
            const payload = {
                email: values.email ?? '',
                phone: values.phone ?? '',
            };
            if (values.password?.trim()) {
                payload.password = values.password;
            }
            await updateUser(current.id, payload);
            const updated = await getUserById(current.id);
            this.setState({ profile: updated });
            setAuth(getToken(), updated);
            message.success('保存成功');
            this.formRef.current?.setFieldValue('password', '');
            this.props.onClose?.();
        } catch (e) {
            message.error(e.message || '保存失败');
        } finally {
            this.setState({ submitting: false });
        }
    };

    render() {
        const { open, onClose, isMobile } = this.props;
        const { loading, submitting, profile } = this.state;

        return (
            <Drawer
                title="账号设置"
                placement={isMobile ? 'bottom' : 'right'}
                height={isMobile ? '85%' : undefined}
                width={drawerWidth(420)}
                open={open}
                onClose={onClose}
                destroyOnClose
            >
                {profile && (
                    <Descriptions column={1} size="small" style={{ marginBottom: 20 }}>
                        <Descriptions.Item label="登录名">{profile.loginId}</Descriptions.Item>
                        <Descriptions.Item label="角色">
                            <Tag color={ROLE_TAG_COLOR[profile.roleKey]}>
                                {displayRoleName(profile)}
                            </Tag>
                        </Descriptions.Item>
                    </Descriptions>
                )}
                <Form ref={this.formRef} layout="vertical" onFinish={this.onFinish}>
                    <Form.Item
                        name="email"
                        label="邮箱"
                        rules={[{ type: 'email', message: '邮箱格式不正确' }]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="邮箱" allowClear />
                    </Form.Item>
                    <Form.Item name="phone" label="手机" rules={[PHONE_RULE]}>
                        <Input prefix={<PhoneOutlined />} placeholder="手机号" maxLength={11} allowClear />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="新密码"
                        extra={PASSWORD_RULE_HINT}
                        rules={[
                            {
                                validator: passwordFormValidator(() => profile?.loginId),
                            },
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="不修改请留空" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button type="primary" htmlType="submit" loading={submitting || loading} block>
                            保存修改
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>
        );
    }
}

export default ProfileEditDrawer;
